import { FC } from 'react';

import { XIcon } from '@heroicons/react/outline';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import { CouponStats } from './types';

interface CouponDetailHeaderProps {
  stats: CouponStats;
  orderId?: string;
  order?: GiftOrderForAdmin;
  previewCopied: boolean;
  setPreviewCopied: (v: boolean) => void;
  onClose: () => void;
}

export const CouponDetailHeader: FC<CouponDetailHeaderProps> = ({
  stats,
  orderId,
  order,
  previewCopied,
  setPreviewCopied,
  onClose,
}) => {
  return (
    <div className="flex shrink-0 items-center justify-between px-5 py-3">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold text-gray-900">쿠폰 상세내역</h2>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>
            전체 <span className="font-semibold text-gray-700">{stats.total}</span>
            {stats.additionalTotal > 0 && (
              <span className="ml-0.5 font-semibold text-amber-600">
                (+{stats.additionalTotal}재발행)
              </span>
            )}
            {stats.reissuedCount > 0 && (
              <span className="ml-0.5 font-semibold text-teal-600">
                (+{stats.reissuedCount}재발급)
              </span>
            )}
          </span>
          {stats.disposedCount > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span>
                폐기{' '}
                <span className="font-semibold text-slate-500">
                  {stats.disposedCount}
                </span>
              </span>
            </>
          )}
          <span className="text-gray-300">|</span>
          <span>
            미공유{' '}
            <span
              className={`font-semibold ${
                stats.notShared > 0 ? 'text-orange-600' : 'text-gray-400'
              }`}
            >
              {stats.notShared}
            </span>
          </span>
          <span className="text-gray-300">|</span>
          <span>
            미사용{' '}
            <span
              className={`font-semibold ${
                stats.available > 0 ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              {stats.available}
            </span>
          </span>
          <span className="text-gray-300">|</span>
          <span>
            교환{' '}
            <span
              className={`font-semibold ${
                stats.delivered > 0 ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              {stats.delivered}
            </span>
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {orderId && order && (
          <button
            onClick={() => {
              const name = encodeURIComponent(order.receiverName ?? '');
              const phone = encodeURIComponent(order.receiverPhoneNumber ?? '');
              const key = encodeURIComponent(
                process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '',
              );
              const bugoId = order.bugo?._id;
              const bugoQuery = bugoId
                ? `&bugoId=${encodeURIComponent(bugoId)}`
                : '';
              const url = `${window.location.origin}/gift-store/order/${orderId}/coupon?name=${name}&phoneNumber=${phone}&secretKey=${key}${bugoQuery}`;
              navigator.clipboard.writeText(url).then(() => {
                setPreviewCopied(true);
                setTimeout(() => setPreviewCopied(false), 2000);
              });
            }}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              previewCopied
                ? 'bg-green-50 text-green-600'
                : 'text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            {previewCopied ? '복사됨!' : '미리보기 URL 복사'}
          </button>
        )}
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
